import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, Button, Grid } from '@mui/material';
import PizzaPreview from './PizzaPreview';
import type { SliceMode, PizzaSlice, PizzaFlavor, PortionKey } from './PizzaBuilderTypes';

interface PizzaBuilderInlineProps {
  allFlavors: PizzaFlavor[];
  allIngredients?: any[];
  onComplete: (isComplete: boolean, sliceMode: SliceMode, slices: PizzaSlice[]) => void;
}

export default function PizzaBuilderInline({
  allFlavors,
  allIngredients = [],
  onComplete
}: PizzaBuilderInlineProps) {
  const [sliceMode, setSliceMode] = useState<SliceMode>('whole');
  const [slices, setSlices] = useState<PizzaSlice[]>([
    { portion: 'whole', flavorId: '' }
  ]);
  const [activePortion, setActivePortion] = useState<PortionKey>('whole');
  const [pizzaType, setPizzaType] = useState<'predefined' | 'custom'>('predefined');

  // Trigger onComplete whenever slices/mode change
  useEffect(() => {
    const isComplete = slices.every(s => s.flavorId !== '' || (s.ingredients && s.ingredients.length > 0));
    onComplete(isComplete, sliceMode, slices);
  }, [slices, sliceMode, onComplete]);

  const handleSliceModeChange = (mode: SliceMode) => {
    setSliceMode(mode);
    const baseFlavor = pizzaType === 'custom' 
      ? { flavorId: armaTuPizzaFlavor.id, flavorName: armaTuPizzaFlavor.name, flavorEmoji: armaTuPizzaFlavor.emoji, ingredients: [] }
      : { flavorId: '', ingredients: [] };

    if (mode === 'whole') {
      setSlices([{ portion: 'whole', ...baseFlavor }]);
      setActivePortion('whole');
    } else if (mode === 'half') {
      setSlices([
        { portion: 'left', ...baseFlavor },
        { portion: 'right', ...baseFlavor }
      ]);
      setActivePortion('left');
    } else if (mode === 'quarter') {
      setSlices([
        { portion: 'top-left', ...baseFlavor },
        { portion: 'top-right', ...baseFlavor },
        { portion: 'bottom-left', ...baseFlavor },
        { portion: 'bottom-right', ...baseFlavor }
      ]);
      setActivePortion('top-left');
    }
  };

  const handleFlavorSelect = (flavor: PizzaFlavor) => {
    setSlices(prev =>
      prev.map(s =>
        s.portion === activePortion
          ? { ...s, flavorId: flavor.id, flavorName: flavor.name, flavorEmoji: flavor.emoji, ingredients: [] }
          : s
      )
    );
    // Auto-advance
    if (sliceMode === 'half') {
      if (activePortion === 'left') setActivePortion('right');
    } else if (sliceMode === 'quarter') {
      if (activePortion === 'top-left') setActivePortion('top-right');
      else if (activePortion === 'top-right') setActivePortion('bottom-right');
      else if (activePortion === 'bottom-right') setActivePortion('bottom-left');
    }
  };

  const handleIngredientToggle = (ingredientId: string) => {
    setSlices(prev =>
      prev.map(s => {
        if (s.portion === activePortion) {
          const currentIngs = s.ingredients || [];
          const isSelected = currentIngs.includes(ingredientId);
          
          if (!isSelected && currentIngs.length >= 2) {
            // Cannot select more than 2 ingredients per side
            return s;
          }

          const newIngs = isSelected ? currentIngs.filter(i => i !== ingredientId) : [...currentIngs, ingredientId];
          
          let newFlavorName = armaTuPizzaFlavor.name;
          if (newIngs.length > 0) {
            const ingNames = newIngs.map(id => allIngredients.find(i => i.id === id)?.name).filter(Boolean);
            if (ingNames.length > 0) {
              newFlavorName = ingNames.join(', ');
            }
          }

          return { ...s, ingredients: newIngs, flavorName: newFlavorName };
        }
        return s;
      })
    );
  };

  const portionTranslations: Record<string, string> = {
    'whole': 'Entera',
    'left': 'Mitad Izquierda',
    'right': 'Mitad Derecha',
    'top-left': 'Sup. Izquierda',
    'top-right': 'Sup. Derecha',
    'bottom-left': 'Inf. Izquierda',
    'bottom-right': 'Inf. Derecha'
  };

  const armaTuPizzaFlavor: PizzaFlavor = {
    id: 'build-your-own',
    name: 'Personalizada',
    emoji: '🛠️',
    color: '#4f46e5',
    description: 'Arma tu propia pizza',
    tier: 0 as any
  };

  const handlePizzaTypeChange = (type: 'predefined' | 'custom') => {
    setPizzaType(type);
    if (type === 'custom') {
      setSliceMode('whole');
      setSlices([{ portion: 'whole', flavorId: armaTuPizzaFlavor.id, flavorName: armaTuPizzaFlavor.name, flavorEmoji: armaTuPizzaFlavor.emoji, ingredients: [] }]);
      setActivePortion('whole');
    } else {
      setSliceMode('whole');
      setSlices([{ portion: 'whole', flavorId: '' }]);
      setActivePortion('whole');
    }
  };

  const extendedFlavors = [armaTuPizzaFlavor, ...allFlavors];
  
  const groupedFlavors = {
    1: allFlavors.filter(f => f.tier === 1),
    2: allFlavors.filter(f => f.tier === 2),
    3: allFlavors.filter(f => f.tier === 3),
  };

  const TIER_LABELS: Record<number, string> = {
    1: '1-2 Sabores',
    2: '3-4 Sabores',
    3: '5 Sabores',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 3, pt: 3, borderTop: '1px solid #f1f5f9' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <PizzaPreview sliceMode={sliceMode} slices={slices} flavors={extendedFlavors} ingredients={allIngredients} size={200} />

        <Stack direction="row" spacing={1} sx={{ mt: 3, mb: 1 }}>
          <Button
            size="small"
            variant={pizzaType === 'predefined' ? 'contained' : 'outlined'}
            onClick={() => handlePizzaTypeChange('predefined')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Sabores Predefinidos
          </Button>
          <Button
            size="small"
            color="secondary"
            variant={pizzaType === 'custom' ? 'contained' : 'outlined'}
            onClick={() => handlePizzaTypeChange('custom')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Personalizado
          </Button>
        </Stack>

        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 1, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          División
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant={sliceMode === 'whole' ? 'contained' : 'outlined'}
            onClick={() => handleSliceModeChange('whole')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Entera
          </Button>
          <Button
            size="small"
            variant={sliceMode === 'half' ? 'contained' : 'outlined'}
            onClick={() => handleSliceModeChange('half')}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Mitades
          </Button>
        </Stack>

        <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 3, mb: 1, color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Seleccionando porción
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent="center">
          {slices.map(s => (
            <Button
              key={s.portion}
              size="small"
              variant={activePortion === s.portion ? 'contained' : 'outlined'}
              onClick={() => setActivePortion(s.portion)}
              sx={{ mb: 1, textTransform: 'none', borderRadius: 2 }}
              color={s.flavorId ? 'success' : 'primary'}
            >
              {portionTranslations[s.portion]} {s.flavorName ? `(${s.flavorName})` : ''}
            </Button>
          ))}
        </Stack>
      </Box>

      <Box sx={{ flex: 1, maxHeight: '520px', overflowY: 'auto', p: 1, borderLeft: { md: '1px solid #f1f5f9' }, pl: { md: 3 } }}>
        {pizzaType === 'predefined' ? (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#0f172a' }}>
              Elige sabor para: <span style={{ color: '#3b82f6' }}>{portionTranslations[activePortion]}</span>
            </Typography>
            {([1, 2, 3] as const).map(tier => {
              const flavorsInTier = groupedFlavors[tier];
              if (!flavorsInTier.length) return null;
              
              return (
                <Box key={tier} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem', fontWeight: 800 }}>
                    {TIER_LABELS[tier]}
                  </Typography>
                  <Grid container spacing={1.5}>
                    {flavorsInTier.map(flavor => (
                      <Grid size={{ xs: 12, sm: 6 }} key={flavor.id}>
                        <Box
                          onClick={() => handleFlavorSelect(flavor)}
                          sx={{
                            p: 1.5,
                            border: '2px solid',
                            borderColor: slices.find(s => s.portion === activePortion)?.flavorId === flavor.id ? 'primary.main' : '#e2e8f0',
                            borderRadius: 2,
                            cursor: 'pointer',
                            bgcolor: slices.find(s => s.portion === activePortion)?.flavorId === flavor.id ? 'rgba(59,130,246,0.05)' : '#fff',
                            '&:hover': { borderColor: 'primary.light', transform: 'translateY(-2px)' },
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}
                        >
                          <Typography fontSize="1.2rem">{flavor.emoji}</Typography>
                          <Typography fontWeight="bold" fontSize="0.85rem" sx={{ color: '#334155' }}>
                            {flavor.name}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </>
        ) : (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2, color: '#0f172a' }}>
              Agrega ingredientes para: <span style={{ color: '#3b82f6' }}>{portionTranslations[activePortion]}</span> <span style={{ fontSize: '0.85rem', color: '#64748b' }}>(Máx. 2)</span>
            </Typography>
            <Grid container spacing={1.5}>
              {allIngredients.map((ing) => {
                const activeSlice = slices.find(s => s.portion === activePortion);
                const isSelected = activeSlice?.ingredients?.includes(ing.id);
                return (
                  <Grid size={{ xs: 12, sm: 6 }} key={ing.id}>
                    <Box
                      onClick={() => handleIngredientToggle(ing.id)}
                      sx={{
                        p: 1.5,
                        border: '2px solid',
                        borderColor: isSelected ? 'secondary.main' : '#e2e8f0',
                        borderRadius: 2,
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'rgba(236,72,153,0.05)' : '#fff',
                        '&:hover': { borderColor: 'secondary.light', transform: 'translateY(-2px)' },
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <Typography fontSize="1.2rem">{ing.emoji}</Typography>
                      <Typography fontWeight="bold" fontSize="0.85rem" sx={{ color: '#334155' }}>
                        {ing.name}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
      </Box>
    </Box>
  );
}
